import { useState, useEffect, useCallback, useMemo, useRef, type Dispatch, type SetStateAction } from 'react';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { convertToSceneOrigin, convertToSceneInstance, convertToProjectInstance } from '@/utils/dataConverters';
import { SceneOrigin, SceneInstance, SupabaseSceneInstance, SupabaseSceneOrigin } from '@/types/sceneTypes';
import { ProjectInstance, SupabaseProjectInstance } from '@/types/projectTypes';
import { fetchSceneRows, fetchStoryRow } from '@/modules/databaseService/scenes';
import { fetchProjectRow } from '@/modules/databaseService/projects';
import { routeMissing } from '@/infrastructure/api/v1/screenReads';
import { getPublishedDevices } from '@/components/publish-flow/deviceAvailability';
import type { PublishDevice } from '@/components/publish-flow/types';
import { fromSceneRow, pickCurrentVersion, pickNewestDraft, sortNewestFirst } from '@/modules/storyData/versions';
import { readStoryWorkspace } from '@/modules/storyData/storyWorkspace';
import { pickDisplayedImage } from '@/modules/uiImages/pickDisplayedImage';
import { uiImageFromDto } from '@/modules/uiImages/uiImagesService';
import type { UIImage } from '@/modules/uiImages/types';
import { seedLiveShareLink } from '@/hooks/useShareLink';
import { sceneToLegacy } from '@/infrastructure/api/v1/mappers/scenes';
import { storyToLegacy } from '@/infrastructure/api/v1/mappers/stories';
import { projectToLegacy } from '@/infrastructure/api/v1/mappers/projects';

export interface StoryVersion {
  id: string;
  version: string;
  status: 'draft' | 'live' | 'archived' | 'pending' | 'locked' | 'template';
  publishedAt: Date;
  changes: string;
  versionName?: string;
  sceneStatus?: string;
  versionNumber?: number;
  // Platforms this version was published/submitted to, from
  // scene_instance_payload.devices. Null for drafts and legacy payloads.
  devices?: PublishDevice[] | null;
}

export interface StoryEditUIImages {
  coverImageUrl: string | null;
  coverImageOriginalUrl: string | null;
  coverImageCrop: { x: number; y: number; width: number; height: number } | null;
  scanImageUrl: string | null;
  scanImageOriginalUrl: string | null;
  scanImageCrop: { x: number; y: number; width: number; height: number } | null;
  anchorImageUrl: string | null;
  anchorImageOriginalUrl: string | null;
  anchorImageCrop: { x: number; y: number; width: number; height: number } | null;
}

function buildVersionsFromSceneInstances(
  rawInstances: Array<{
    scene_instance_id: string;
    scene_instance_display_name?: string;
    updated_at?: string;
    scene_instance_version_number?: number;
    scene_instance_status?: string;
    scene_instance_payload?: Record<string, unknown> | string | null;
  }>
): { versions: StoryVersion[]; draftCount: number; latestDraftSceneInstanceId: string | null } {
  if (!rawInstances?.length) {
    return { versions: [], draftCount: 0, latestDraftSceneInstanceId: null };
  }
  const sorted = sortNewestFirst(rawInstances, fromSceneRow);
  const allVersions: StoryVersion[] = sorted.map((scene) => {
    const status = (scene.scene_instance_status || 'draft') as StoryVersion['status'];
    const displayName = scene.scene_instance_display_name || `${status} Version`;
    return {
      id: scene.scene_instance_id,
      version: displayName,
      status: status === 'template' ? 'template' : status,
      publishedAt: new Date(scene.updated_at || Date.now()),
      changes: `Version Name: ${displayName}`,
      versionName: displayName,
      sceneStatus: scene.scene_instance_status,
      versionNumber: scene.scene_instance_version_number,
      devices: getPublishedDevices(scene.scene_instance_payload)
    };
  });
  const draftCount = sorted.filter(
    (s) => s.scene_instance_status === 'draft' || s.scene_instance_status === 'template'
  ).length;
  return {
    versions: allVersions,
    draftCount,
    latestDraftSceneInstanceId: pickNewestDraft(sorted, fromSceneRow)?.scene_instance_id ?? null
  };
}

interface StoryEditSetters {
  setSceneOrigin: Dispatch<SetStateAction<SceneOrigin | null>>;
  setSceneInstances: Dispatch<SetStateAction<SceneInstance[]>>;
  setCurrentSceneInstance: Dispatch<SetStateAction<SceneInstance | null>>;
  setProject: Dispatch<SetStateAction<ProjectInstance | null>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setLoadError: Dispatch<SetStateAction<string | null>>;
  setVersions: Dispatch<SetStateAction<StoryVersion[]>>;
  setDraftCount: Dispatch<SetStateAction<number>>;
  setLatestDraftSceneInstanceId: Dispatch<SetStateAction<string | null>>;
  setHasImmersalScene: Dispatch<SetStateAction<boolean>>;
  setHasGeospatialAnchor: Dispatch<SetStateAction<boolean>>;
  setHasImageAnchorScene: Dispatch<SetStateAction<boolean>>;
  showUIImages: (images: UIImage[]) => void;
  fetchUIImages: (sceneOriginId: string) => Promise<boolean>;
}

/** Shows the story's versions: the list, the one picked, the anchors they have, and the drafts. */
function showVersions(rows: SupabaseSceneInstance[], silent: boolean, set: StoryEditSetters) {
  const instances = rows.map(convertToSceneInstance);
  const active = instances.filter((i) => i.sceneInstanceStatus !== 'archived');
  const currentId = pickCurrentVersion(rows, fromSceneRow)?.scene_instance_id;
  set.setSceneInstances(instances);
  set.setCurrentSceneInstance(prev =>
    (silent ? active.find(i => i.sceneId === prev?.sceneId) : undefined) ?? instances.find(i => i.sceneId === currentId) ?? null);
  set.setHasImmersalScene(active.some((i) => i.sceneInstanceAnchorCategory === 'immersal'));
  set.setHasGeospatialAnchor(active.some((i) => i.sceneInstanceAnchorCategory === 'geospatial_anchor'));
  set.setHasImageAnchorScene(active.some((i) => i.sceneInstanceAnchorCategory === 'image_anchor'));
  const { versions: v, draftCount: dc, latestDraftSceneInstanceId: latest } = buildVersionsFromSceneInstances(rows);
  set.setVersions(v);
  set.setDraftCount(dc);
  set.setLatestDraftSceneInstanceId(latest);
}

/** The categories the page shows: the cover, the scan image and the anchor image. */
const STORY_IMAGE_CATEGORIES = new Set(['SceneLandscape', 'ScanImage', 'ImageAnchor']);

export const STORY_LOAD_FAILED = "We couldn't load this story. Check your connection and try again.";

// Only a story that is gone or not the user's reads as not found; anything else is a failure worth retrying
const isNotFound = (error: { status?: number } | null) => error?.status === 404 || error?.status === 403;

/**
 * The story edit page read part by part (getStory, listScenes, getProject, listImages), for a backend without
 * getStoryWorkspace (rolled back to before it). The share link and the template status read their own.
 */
async function readStoryEditDataPartByPart(storyId: string, userId: string, silent: boolean, set: StoryEditSetters, isLatest: () => boolean) {
  const response = await fetchStoryRow(storyId);
  if (!isLatest()) return null;

  if (response.error) {
    console.error('Error fetching scene origin:', response.error);
    if (isNotFound(response.error)) {
      set.setSceneOrigin(null);
      set.setLoadError(null);
    } else if (!silent) {
      set.setLoadError(STORY_LOAD_FAILED);
    }
    return null;
  }
  // Only the user's own story opens here, as the user_id filter did before
  if (response.data.user_id !== userId) {
    set.setSceneOrigin(null);
    set.setLoadError(null);
    return null;
  }

  const scene = convertToSceneOrigin(response.data as unknown as SupabaseSceneOrigin);
  set.setSceneOrigin(scene);
  set.setLoadError(null);

  const [sceneInstancesResponse, projectResult] = await Promise.all([
    fetchSceneRows({ storyUniqueId: [scene.sceneOriginId], sort: 'versionNumber' }),
    // A story outside a project reads as missing, as filtering on a null id did before
    scene.projectInstanceId
      ? fetchProjectRow(scene.projectInstanceId)
      : Promise.resolve({ data: null, error: new Error('Project not found') })
  ]);
  if (!isLatest()) return null;
  const projectResponse = projectResult.error
    ? projectResult
    : projectResult.data.user_id === userId
      ? projectResult
      : { data: null, error: new Error('Project not found') };

  if (sceneInstancesResponse.error) {
    console.error('Error fetching scene instances:', sceneInstancesResponse.error);
    if (!silent) set.setLoadError(STORY_LOAD_FAILED);
  } else {
    showVersions(sceneInstancesResponse.data as unknown as SupabaseSceneInstance[], silent, set);
  }

  if (projectResponse.error) {
    console.error('Error fetching project:', projectResponse.error);
    const status = (projectResponse.error as { status?: number }).status;
    if (status === undefined || isNotFound({ status })) set.setProject(null);
    else if (!silent) set.setLoadError(STORY_LOAD_FAILED);
  } else {
    set.setProject(convertToProjectInstance(projectResponse.data as unknown as SupabaseProjectInstance));
  }

  const imagesRead = await set.fetchUIImages(scene.sceneOriginId);
  if (!imagesRead && !silent && isLatest()) set.setLoadError(STORY_LOAD_FAILED);

  return { scene, sceneInstancesResponse };
}

/**
 * Reads the story, its versions, its project and its images into state, in one read (getStoryWorkspace), then stops
 * loading; callers show loading first when they want it. A live refresh (silent) keeps the version picked. The same read
 * carries the live version's link, kept for the share link to show.
 */
async function readStoryEditData(
  storyId: string | undefined,
  userId: string | undefined,
  silent: boolean,
  set: StoryEditSetters,
  isLatest: () => boolean,
) {
  if (!storyId || !userId) return;
  try {
    const { data: workspace, error } = await readStoryWorkspace(storyId);
    if (!isLatest()) return null;
    if (routeMissing(error)) return await readStoryEditDataPartByPart(storyId, userId, silent, set, isLatest);

    if (error) {
      console.error('Error fetching scene origin:', error);
      if (isNotFound(error)) {
        set.setSceneOrigin(null);
        set.setLoadError(null);
      } else if (!silent) {
        set.setLoadError(STORY_LOAD_FAILED);
      }
      return null;
    }
    // Only the user's own story opens here, as the user_id filter did before
    const story = storyToLegacy(workspace.story);
    if (story.user_id !== userId) {
      set.setSceneOrigin(null);
      set.setLoadError(null);
      return null;
    }

    const scene = convertToSceneOrigin(story as unknown as SupabaseSceneOrigin);
    set.setSceneOrigin(scene);
    set.setLoadError(null);

    const rows = workspace.scenes.map(sceneToLegacy) as unknown as SupabaseSceneInstance[];
    showVersions(rows, silent, set);

    // A story outside a project, or a project that isn't the user's, reads as missing
    const project = workspace.project;
    set.setProject(project && project.userUniqueId === userId
      ? convertToProjectInstance(projectToLegacy(project) as unknown as SupabaseProjectInstance)
      : null);

    set.showUIImages(workspace.images.filter(image => STORY_IMAGE_CATEGORIES.has(image.category ?? '')).map(uiImageFromDto));

    // The owner's live link, as the share link would read it for the live version
    if (workspace.shareLinks) {
      for (const live of rows.filter(row => row.scene_instance_status === 'live')) {
        seedLiveShareLink(live.scene_instance_id, scene.sceneOriginId, live.scene_instance_payload as Record<string, unknown> | string | null, workspace.shareLinks);
      }
    }

    return { scene, sceneInstancesResponse: { data: rows, error: null } };
  } catch (error) {
    console.error('Error fetching scene origin:', error);
    if (isLatest() && !silent) set.setLoadError(STORY_LOAD_FAILED);
    return null;
  } finally {
    if (isLatest()) set.setLoading(false);
  }
}

export function useStoryEditData(storyId: string | undefined, userId: string | undefined) {
  const services = useServices();

  const [sceneOrigin, setSceneOrigin] = useState<SceneOrigin | null>(null);
  const [sceneInstances, setSceneInstances] = useState<SceneInstance[]>([]);
  const [currentSceneInstance, setCurrentSceneInstance] = useState<SceneInstance | null>(null);
  const [project, setProject] = useState<ProjectInstance | null>(null);
  const [loading, setLoading] = useState(true);
  const [versions, setVersions] = useState<StoryVersion[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [draftCount, setDraftCount] = useState(0);
  const [latestDraftSceneInstanceId, setLatestDraftSceneInstanceId] = useState<string | null>(null);
  const [hasImmersalScene, setHasImmersalScene] = useState(false);
  const [hasGeospatialAnchor, setHasGeospatialAnchor] = useState(false);
  const [hasImageAnchorScene, setHasImageAnchorScene] = useState(false);

  // UI images state
  const [uiImages, setUiImages] = useState<StoryEditUIImages>({
    coverImageUrl: null,
    coverImageOriginalUrl: null,
    coverImageCrop: null,
    scanImageUrl: null,
    scanImageOriginalUrl: null,
    scanImageCrop: null,
    anchorImageUrl: null,
    anchorImageOriginalUrl: null,
    anchorImageCrop: null
  });

  const [imageIds, setImageIds] = useState<ReadonlySet<string>>(() => new Set());
  const [loadError, setLoadError] = useState<string | null>(null);
  const imagesSeq = useRef(0);

  // The story's images as the page shows them, newest first
  const applyUIImages = useCallback((images: UIImage[]) => {
    const cover = pickDisplayedImage(images, 'SceneLandscape');
    const scan = pickDisplayedImage(images, 'ScanImage');
    const anchor = pickDisplayedImage(images, 'ImageAnchor');
    setImageIds(new Set(images.map((img) => img.imageId)));
    setUiImages({
      coverImageUrl: cover?.imageCroppedUrl || cover?.imageOriginalUrl || null,
      coverImageOriginalUrl: cover?.imageOriginalUrl || null,
      coverImageCrop: cover?.imageMetadata?.crop || null,
      scanImageUrl: scan?.imageCroppedUrl || scan?.imageOriginalUrl || null,
      scanImageOriginalUrl: scan?.imageOriginalUrl || null,
      scanImageCrop: scan?.imageMetadata?.crop || null,
      anchorImageUrl: anchor?.imageCroppedUrl || anchor?.imageOriginalUrl || null,
      anchorImageOriginalUrl: anchor?.imageOriginalUrl || null,
      anchorImageCrop: anchor?.imageMetadata?.crop || null
    });
  }, []);

  // What the page's read answered is the newest: an image read still on its way is dropped
  const showUIImages = useCallback((images: UIImage[]) => {
    ++imagesSeq.current;
    applyUIImages(images);
  }, [applyUIImages]);

  // A failed read keeps the images shown: blanking them would say the story has none
  const fetchUIImages = useCallback(
    async (sceneOriginId: string): Promise<boolean> => {
      if (!sceneOriginId) return false;
      const seq = ++imagesSeq.current;
      try {
        const response = await services.uiImages.getUIImagesForSceneOrigin(sceneOriginId);
        if (seq !== imagesSeq.current) return true;
        if (!response.success || !response.data) {
          console.error('Error fetching UI images:', response.error);
          return false;
        }
        applyUIImages(response.data);
        return true;
      } catch (error) {
        console.error('Error fetching UI images:', error);
        return false;
      }
    },
    [services.uiImages, applyUIImages]
  );

  const fetchVersionHistory = useCallback(
    async (sceneOriginId: string) => {
      if (!sceneOriginId) return;
      try {
        setLoadingVersions(true);
        const { data, error } = await fetchSceneRows({ storyUniqueId: [sceneOriginId], sort: '-versionNumber' });
        const instances = data as unknown as SupabaseSceneInstance[] | null;
        if (error || !instances) {
          if (error) console.error('Error fetching scene instances:', error);
          return;
        }
        const { versions: v, draftCount: dc, latestDraftSceneInstanceId: latest } = buildVersionsFromSceneInstances(instances);
        setVersions(v);
        setDraftCount(dc);
        setLatestDraftSceneInstanceId(latest);
      } catch (error) {
        console.error('Error fetching version history:', error);
      } finally {
        setLoadingVersions(false);
      }
    },
    []
  );

  // A new story or user shows loading, set as the inputs change rather than in the effect that reads
  const [readFor, setReadFor] = useState({ storyId, userId });
  if (readFor.storyId !== storyId || readFor.userId !== userId) {
    setReadFor({ storyId, userId });
    setLoading(true);
  }

  const setters = useMemo(() => ({
    setSceneOrigin, setSceneInstances, setCurrentSceneInstance, setProject, setLoading, setLoadError, setVersions, setDraftCount,
    setLatestDraftSceneInstanceId, setHasImmersalScene, setHasGeospatialAnchor, setHasImageAnchorScene, showUIImages, fetchUIImages,
  }), [showUIImages, fetchUIImages]);
  const readSeq = useRef(0);
  const read = useCallback((silent: boolean) => {
    const seq = ++readSeq.current;
    return readStoryEditData(storyId, userId, silent, setters, () => seq === readSeq.current);
  }, [storyId, userId, setters]);

  // A fresh read asked for shows loading first
  const loadData = useCallback(() => {
    setLoading(true);
    return read(false);
  }, [read]);
  const refreshInPlace = useCallback(() => read(true), [read]);

  useEffect(() => {
    void read(false);
  }, [read]);

  return {
    sceneOrigin,
    sceneInstances,
    currentSceneInstance,
    project,
    loading,
    loadError,
    versions,
    loadingVersions,
    draftCount,
    latestDraftSceneInstanceId,
    hasImmersalScene,
    hasGeospatialAnchor,
    hasImageAnchorScene,
    uiImages,
    imageIds,
    setSceneOrigin,
    setSceneInstances,
    setCurrentSceneInstance,
    setProject,
    setVersions,
    setDraftCount,
    setLatestDraftSceneInstanceId,
    setUiImages,
    fetchUIImages,
    fetchVersionHistory,
    refetch: loadData,
    refreshInPlace
  };
}
